// A minimal History-API path router. Screens return an element that replaces
// the app root; `data-link` anchors are intercepted for client-side nav.

import { errText } from "./dom.ts";
import { brandLockup, BRAND_NAME } from "./ui/brand.ts";
import { t } from "./i18n/index.ts";

export type Params = Record<string, string>;
export type Screen = (params: Params) => Promise<HTMLElement> | HTMLElement;

interface RouteDef {
  pattern: RegExp;
  keys: string[];
  screen: Screen;
}

const routes: RouteDef[] = [];

// Bumped on every render(); a render that a re-entrant navigate() has superseded
// (e.g. homeScreen redirecting to /login mid-render) sees a stale token and
// declines to commit, so the latest navigation always wins the DOM.
let renderToken = 0;
let leaveGuard: (() => boolean) | undefined;
let currentPath = location.pathname;

/** An unfinished local board owns this guard until it is destroyed. */
export function guardNavigation(guard: () => boolean): () => void {
  leaveGuard = guard;
  const unload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
  window.addEventListener("beforeunload", unload);
  return () => {
    if (leaveGuard === guard) leaveGuard = undefined;
    window.removeEventListener("beforeunload", unload);
  };
}

/** The current render generation. An async screen captures this at entry and
 *  passes it to shared, module-level state (e.g. the analysis board's `active`
 *  controller) so that, after an `await`, it can tell whether a newer navigation
 *  has already superseded it — and decline to tear down the newer screen. */
export function currentToken(): number {
  return renderToken;
}

/** Whether `token` is still the latest render (i.e. not yet superseded). */
export function isCurrent(token: number): boolean {
  return token === renderToken;
}

/** Register a route. `path` may contain `:name` segments, e.g. `/i/:token`. */
export function route(path: string, screen: Screen): void {
  const keys: string[] = [];
  const source = path.replace(/:[^/]+/g, (m) => {
    keys.push(m.slice(1));
    return "([^/]+)";
  });
  routes.push({ pattern: new RegExp(`^${source}/?$`), keys, screen });
}

/** Navigate to `path` (pushes history) and render. */
export function navigate(path: string): void {
  if (leaveGuard && !leaveGuard()) return;
  if (path !== location.pathname) {
    history.pushState({}, "", path);
  }
  currentPath = path;
  void render();
}

async function render(): Promise<void> {
  const app = document.querySelector<HTMLElement>("#app");
  if (!app) return;

  const token = ++renderToken;
  for (const r of routes) {
    const m = r.pattern.exec(location.pathname);
    if (!m) continue;
    const params: Params = {};
    r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1] ?? "")));
    try {
      const node = await r.screen(params);
      // A screen may redirect synchronously (e.g. homeScreen → /login), starting
      // a newer render; only commit if we are still the latest one.
      if (token !== renderToken) return;
      app.replaceChildren(node);
      // Let app-wide widgets (e.g. the turn-alert pill) react to a screen change.
      window.dispatchEvent(new Event("spa:navigated"));
    } catch (err) {
      // Surface the real error as text in the console (the on-screen card only
      // shows the message), so a failing screen is debuggable.
      console.error(`[screen ${location.pathname}] ${errText(err)}`);
      if (token !== renderToken) return;
      app.replaceChildren(errorCard(err));
    }
    return;
  }
  // No match → home.
  navigate("/");
}

function errorCard(err: unknown): HTMLElement {
  const card = document.createElement("main");
  card.className = "card";
  card.innerHTML = `<h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
    <p class="msg bad"></p>
    <p><a data-link href="/">${t("common.backHome")}</a></p>`;
  card.querySelector(".msg")!.textContent = err instanceof Error ? err.message : t("common.somethingWrong");
  return card;
}

/** Wire up popstate + link interception and render the first view. */
export function startRouter(): void {
  window.addEventListener("popstate", () => {
    if (leaveGuard && !leaveGuard()) {
      history.pushState({}, "", currentPath);
      return;
    }
    currentPath = location.pathname;
    void render();
  });
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    const target = e.target as HTMLElement | null;
    const a = target?.closest("a[data-link]");
    if (a instanceof HTMLAnchorElement && a.origin === location.origin) {
      e.preventDefault();
      navigate(a.pathname);
    }
  });
  void render();
}
